/* eslint-disable max-len */
import { createGlobalStyle } from 'styled-components';
import { maxWidth } from 'config/constants';

export const KCCThemeGlobalStyles = createGlobalStyle`
  && .kcc-theme-tooltip {
    .ant-tooltip-inner {
      padding: 8px;
    }
  }

  && .kcc-theme-input:focus,
  && .kcc-theme-input:focus-within,
  && .kcc-theme-select.kcc-theme-select:focus-within .ant-select-selector,
  && .kcc-theme-select.kcc-theme-select.ant-select-focused .ant-select-selector {
    outline: 2px solid ${(props): string => props.theme.kcc.colors.BLUE[500]} !important;
    outline-offset: 2px !important;
    box-shadow: none !important; 
    transition: outline-offset 0.2s ease, outline-color 0.2s ease !important;
  }

  && .kcc-theme-select.kcc-theme-select:not(.ant-select-disabled) .ant-select-arrow,
  && .kcc-theme-select.kcc-theme-select:not(.ant-select-disabled).ant-select-focused .ant-select-arrow {
    color: ${(props): string => props.theme.kcc.colors.BLACK};
  }

  && .kcc-theme-select .ant-select-clear {
    right: 18px;
    width: 22px;
    height: 22px;
  }

  /*
   * antd 5.x bug (fixed upstream in 6.3.7 via ant-design#57705, "Fix InputNumber selection
   * highlight radius" — not available on our antd 5.23.4): the inner <input> inherits the same
   * border-radius as the outer pill wrapper. That inner radius clips content rendered at its own
   * edge — the caret when the field is empty, or the text-selection highlight — even though the
   * wrapper itself has enough padding. Backported here until we upgrade to antd 6.
   */
  && .ant-input-number-input {
    border-radius: 0 !important;
  }

  && .ant-picker.ant-picker-disabled,
  && .ant-picker.ant-picker-disabled:hover {
    svg {
      path {
        fill: ${({ theme: { kcc } }): string => kcc.colors.TEXT_DISABLED} !important;
      }
    }
    
    border-color: ${(props): string => props.theme.kcc.colors.GRAY_200} !important;
  }

  && .ant-input-number-affix-wrapper.ant-input-number-disabled,
  && .ant-input-number-affix-wrapper.ant-input-number-disabled:hover {
    border-color: ${(props): string => props.theme.kcc.colors.GRAY_200} !important;
  }

  && .ant-input-number-disabled .ant-input-number-suffix .ant-typography {
    color: ${(props): string => props.theme.kcc.colors.GRAY_400};
  }

  .ant-select-dropdown {
    border: ${({ theme: { kcc } }): number => kcc.lineWidth.BORDER}px solid ${({ theme: { kcc } }): string =>
  kcc.colors.BORDER};
    box-shadow: 0 0 0 0 #000;
    padding: 0;

    .rc-virtual-list-holder {
      max-height: 304px !important;

      .ant-select-item {
        min-height: fit-content;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        display: block;
      }

      .ant-select-item-group {
        padding: 7px 12px;
        font-size: ${({ theme: { kcc } }): number => kcc.typography.FONT_SIZE.SM}px;
      }

      .ant-select-item-option-grouped {
        padding: 10px 20px;

        .svg-icon {
          width: ${({ theme: { kcc } }): number => kcc.size.S250}px;
          height: ${({ theme: { kcc } }): number => kcc.size.S250}px;
        }

        span.ant-typography {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          max-width: 90%;
          display: block;
        }
      }
    }
  }
  
  && .ant-notification-notice-wrapper:has(.kcc-toast) {
    height: auto !important;
    min-height: 52px;
    border-radius: 0;
    margin-bottom: ${(props): string => props.theme.kcc.spacing.XS};
  }

  && .kcc-toast {
    font-family: ${(props): string => props.theme.kcc.typography.FONT_FAMILY};
    padding: 0;
    overflow: hidden;

    &.ant-notification-notice {
      position: relative;
      padding: 0 !important;
    }

    .ant-notification-notice-content {
      padding: ${(props): string => props.theme.kcc.spacing.BASE};
    }

    .ant-notification-notice-icon {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 40px;
      height: 100%;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .svg-icon {
        display: flex;
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .ant-notification-notice-message,
    .ant-notification-notice-description {
      margin-inline-start: 40px !important;
      font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.BASE}px !important;
    }

    .ant-notification-notice-message {
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    }

    .ant-notification-notice-message:empty {
      display: none;
      margin-bottom: 0 !important;
    }

    &.kcc-toast--message-regular .ant-notification-notice-message {
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.REGULAR};
    }

    .ant-notification-notice-with-icon .ant-notification-notice-description {
      margin-left: 0;
    }

    &.ant-notification-notice-closable .ant-notification-notice-message,
    &.ant-notification-notice-closable .ant-notification-notice-description {
      padding-inline-end: 32px;
    }

    .ant-notification-notice-close {
      top: ${(props): string => props.theme.kcc.spacing.BASE} !important;
      inset-inline-end: ${(props): string => props.theme.kcc.spacing.BASE};
      width: 24px;
      height: 24px;
      padding: 0;

      &:hover,
      &:active {
        background-color: transparent !important;
      }

      .svg-icon {
        display: flex;
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  && .kcc-toast:has(.ant-notification-notice-description:empty) .ant-notification-notice-message {
    margin-bottom: 0 !important;
  }

  && .kcc-toast--success .ant-notification-notice-icon { 
    background-color: ${(props): string => props.theme.kcc.colors.SUCCESS};
  }

  && .kcc-toast--error .ant-notification-notice-icon {
    background-color: ${(props): string => props.theme.kcc.colors.ERROR}; 
  }
    
  && .kcc-toast--info .ant-notification-notice-icon {
    background-color: ${(props): string => props.theme.kcc.colors.INFO}; 
  }

  && .kcc-toast--warning .ant-notification-notice-icon { 
    background-color: ${(props): string => props.theme.kcc.colors.WARNING}; 
  }

  && .kcc-toast--loading .ant-notification-notice-icon {
    background-color: ${(props): string => props.theme.kcc.colors.INFO};
  }

  @keyframes kcc-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  && .kcc-toast--loading .ant-notification-notice-icon svg {
    animation: kcc-spin 1s linear infinite;
  }

  && .kcc-theme-modal .ant-modal-close {
    top: 14px;
    right: 20px;
    color: ${(props): string => props.theme.kcc.colors.BLACK};

    svg {
      width: 20px;
      height: 20px;
    }

    ${maxWidth.l} {
      top: 36px;
      right: 23px;
    }
  }

  && .kcc-theme-modal .ant-modal-title {
    font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.HEADING_4}px;

    ${maxWidth.l} {
      font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.HEADING_3}px;
    }
  }

  && .kcc-theme-modal .ant-modal-header {
    padding: 0px 64px 0px 20px;

    ${maxWidth.l} {
      padding: 0px 64px 0px 23px;
    }
  }

  && .kcc-theme-card {
    .ant-card-head, .ant-card-body {
      padding: 16px 24px;
    }
  }
    
  && .kcc-theme-table {
    .ant-table {
      width: 100%;
      border-left: 1px solid ${(props): string => props.theme.kcc.colors.BORDER};
      border-right: 1px solid ${(props): string => props.theme.kcc.colors.BORDER};
    }
  }

  && .kcc-theme-range-picker {
    height: 40px;
    width: fit-content;
    justify-content: center;
    gap: ${(props): string => props.theme.kcc.spacing.XS};

    a.ant-picker-now-btn {
      color: ${(props): string => props.theme.kcc.colors.TEXT};
    }

    .ant-picker-input {
      width: auto;
    }

    .ant-picker-range-separator {
      svg {
        path {
          fill: ${(props): string => props.theme.kcc.colors.TEXT};
        }
      }
    }

    .ant-picker-suffix {
      color: ${(props): string => props.theme.kcc.colors.TEXT};

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  && .kcc-theme-date-picker {
    min-width: 160px;
    height: 40px;

    ${maxWidth.l} {
      min-width: 140px;
    }

    .ant-picker-suffix {
      color: ${(props): string => props.theme.kcc.colors.TEXT};

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .ant-picker-separator {
      color: ${(props): string => props.theme.kcc.colors.TEXT};
    }

    &.ant-picker-focused {
      outline: 2px solid ${(props): string => props.theme.kcc.colors.BLUE[500]};
      outline-offset: 2px;
      box-shadow: none;
    }
  }

  && .kcc-theme-date-picker-popup,
  && .kcc-theme-time-picker-popup {
    margin-top: 4px;

    .ant-picker-panel-container {
      border: 2px solid ${(props): string => props.theme.kcc.colors.BORDER};
      font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.BASE}px;

      .ant-picker-presets {
        border-right: 2px solid ${(props): string => props.theme.kcc.colors.BORDER};
        padding: ${(props): string => props.theme.kcc.spacing.BASE};

        & > ul {
          padding: 0;
          border-inline-end: none;
        }

        & > ul > li {
          padding-block: 6px;
          white-space: normal;
          word-break: break-word;

          &:hover {
            background-color: rgba(0, 0, 0, 0.08);
          }

          &:has([data-preset-active]) {
            font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
            background-color: rgba(0, 0, 0, 0.08);
          }
        }

        & > ul > li + li {
          margin-top: ${(props): string => props.theme.kcc.spacing.XXS};
        }
      }

      a.ant-picker-now-btn {
        color: ${(props): string => props.theme.kcc.colors.TEXT} !important;
      }
    }

    .ant-picker-content,
    .ant-picker-header,
    .ant-picker-header button,
    .ant-picker-cell,
    .ant-picker-cell .ant-picker-cell-inner,
    .ant-picker-time-panel-cell,
    .ant-picker-time-panel-cell-inner,
    .ant-picker-footer,
    .ant-picker-ranges,
    .ant-picker-presets li {
      font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.BASE}px;
    }

    .ant-picker-cell-in-range::before,
    .ant-picker-cell-range-start::before,
    .ant-picker-cell-range-end::before {
      background: ${(props): string => props.theme.kcc.colors.TRANSPARENT};
    }

    .ant-picker-cell-in-range:not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end) .ant-picker-cell-inner {
      box-shadow: inset 0 0 0 2px ${(props): string => props.theme.kcc.colors.BORDER};
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
      background: ${(props): string => props.theme.kcc.colors.TRANSPARENT};
    }

    &.ant-picker-dropdown .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
    &.ant-picker-dropdown .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
      background: ${(props): string => props.theme.kcc.colors.FILL_TERTIARY};
      box-shadow: none;
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
      color: ${(props): string => props.theme.kcc.colors.BLACK};
    }

    .ant-picker-cell:not(.ant-picker-cell-disabled):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end) {
      &:hover .ant-picker-cell-inner {
        box-shadow: inset 0 0 0 2px ${(props): string => props.theme.kcc.colors.BLACK};
        background: ${(props): string => props.theme.kcc.colors.TRANSPARENT};
      }
    }

    .ant-picker-week-panel {
      .ant-picker-week-panel-row {
        td::before {
          background: ${(props): string => props.theme.kcc.colors.TRANSPARENT};
        }

        td.ant-picker-cell:not(.ant-picker-cell-disabled):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end) {
          &:hover .ant-picker-cell-inner {
            box-shadow: none;
          }
        }
      }

      .ant-picker-week-panel-row-selected,
      .ant-picker-week-panel-row-range-start,
      .ant-picker-week-panel-row-range-end {
        td.ant-picker-cell:not(.ant-picker-cell-disabled) {
          &::before {
            background: rgba(0, 0, 0, 0.08);
          }

          .ant-picker-cell-inner {
            background: ${(props): string => props.theme.kcc.colors.FILL_TERTIARY};
            box-shadow: none;
            font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
            color: ${(props): string => props.theme.kcc.colors.BLACK};
          }
        }
      }

      .ant-picker-week-panel-row:hover:not(.ant-picker-week-panel-row-selected):not(
          .ant-picker-week-panel-row-range-start
        ):not(.ant-picker-week-panel-row-range-end) {
        td.ant-picker-cell:not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
          background: ${(props): string => props.theme.kcc.colors.TRANSPARENT};
          box-shadow: none;
        }
      }
    }
  }

  && .kcc-theme-time-picker {
    min-width: 160px;

    ${maxWidth.l} {
      min-width: 140px;
    }

    .ant-picker-suffix {
      color: ${(props): string => props.theme.kcc.colors.TEXT};

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .ant-picker-separator {
      color: ${(props): string => props.theme.kcc.colors.TEXT};
    }

    &.ant-picker-focused {
      outline: 2px solid ${(props): string => props.theme.kcc.colors.BLUE[500]};
      outline-offset: 2px;
      box-shadow: none;
    }
  }

  && .kcc-theme-time-picker-popup {
    margin-top: 4px;

    .ant-picker-time-panel-container {
      width: 232px;

      .ant-picker-time-panel {
        width: 232px;

        .ant-picker-time-panel-cell-selected {
          font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};

          .ant-picker-time-panel-cell-inner {
            background-color: rgba(0, 0, 0, 0.08);
          }
        }

        .ant-picker-time-panel-cell-inner {
          text-align: center;
          padding-inline-start: 0 !important;
          width: 100% !important;
        }
      }

      .ant-picker-footer {
        .ant-picker-ranges {
          width: 100%;
          height: 100%;
          padding: 8px 12px;
        }
        
        .ant-picker-now {
          width: 100%;
          height: 32px;

          .ant-picker-now-btn {
            display: block;
            width: 100px;
            height: 32px;
            text-align: center;
          }
        }

        .ant-picker-ok {
          width: 100%;
          height: 32px;
          margin-inline-start: unset;
          padding-block: 0;

          button {
            width: 100px;
            height: 32px;
          }
        }
      }
    }
  }

  && .kcc-theme-segmented {
    border: ${(props): number => props.theme.kcc.lineWidth.BORDER}px solid ${(props): string =>
  props.theme.kcc.colors.BORDER};

    .ant-segmented-item {
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.REGULAR};
      padding-block: ${({ theme: { kcc } }): string => kcc.spacing.XXS};
      padding-inline: ${({ theme: { kcc } }): string => kcc.spacing.MD};

      .ant-segmented-item-icon {
        svg {
          path {
            fill: ${({ theme: { kcc } }): string => kcc.colors.TEXT};
          }
        }
      }
    }

    .ant-segmented-item-selected {
      font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};

      .ant-segmented-item-icon {
        svg {
          path {
            fill: ${({ theme: { kcc } }): string => kcc.colors.PRIMARY_KARCHER};
          }
        }
      }
    }

    .ant-segmented-item-label {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;

      span {
        margin-inline-start: 0;
      }
    }

    .ant-segmented-item-icon {
      width: 20px;
      height: 20px;
      margin-right: ${({ theme: { kcc } }): string => kcc.spacing.XS};
    }
  }

  && .ant-segmented.ant-segmented-disabled {
    .ant-segmented-item,
    .ant-segmented-item-selected,
    .ant-segmented-thumb {
      color: ${(props): string => props.theme.kcc.colors.GRAY_400};

      svg path {
        fill: ${(props): string => props.theme.kcc.colors.GRAY_400};
      }
    }

    .ant-segmented-item-selected,
    .ant-segmented-thumb {
      background-color: ${(props): string => props.theme.kcc.colors.GRAY_200};
    }
  }

  && .kcc-theme-switch {
    border: ${(props): number => props.theme.kcc.lineWidth.BORDER}px solid ${(props): string =>
  props.theme.kcc.colors.BLACK};
  border-radius: 80px;

    &.ant-switch-checked {
      .ant-switch-handle::before {
        background-color: ${(props): string => props.theme.kcc.colors.WHITE};
      }
    }
  }

  && .ant-switch.ant-switch-disabled {
    opacity: 1;
    background: ${(props): string => props.theme.kcc.colors.GRAY_200};
    border-color: ${(props): string => props.theme.kcc.colors.GRAY_200};

    .ant-switch-handle::before {
      background-color: ${(props): string => props.theme.kcc.colors.WHITE};
    }
  }

`;
